import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { themeOf, rarityOf } from '../lib/classTheme';

/* ============================================================
   WoF COMBAT FX ENGINE v2
   Class-themed particles, screen shake, slash trails,
   floating numbers, ultimate cinematic, status auras.
   ============================================================ */

/* ---------- 1. PARTICLE CANVAS ---------- */
export function ParticleLayer({ trigger, classKey }) {
  const ref = useRef(null);
  const parts = useRef([]);
  const raf = useRef();
  const t = themeOf(classKey);

  useEffect(() => {
    const cvs = ref.current; if (!cvs) return;
    const ctx = cvs.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      cvs.width = cvs.offsetWidth * dpr;
      cvs.height = cvs.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const loop = () => {
      ctx.clearRect(0, 0, cvs.offsetWidth, cvs.offsetHeight);
      parts.current = parts.current.filter(p => p.life > 0);
      for (const p of parts.current) {
        p.x += p.vx; p.y += p.vy; p.vy += p.g; p.life--;
        p.vx *= 0.975; p.vy *= 0.975;
        const a = Math.max(0, p.life / p.max);
        ctx.globalAlpha = a;
        ctx.fillStyle = p.color;
        if (p.shape === 'spark') {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          ctx.fillRect(-p.size * 2.2, -p.size / 3, p.size * 4.4, p.size / 1.6);
          ctx.restore();
        } else if (p.shape === 'star') {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot += 0.15);
          ctx.beginPath();
          for (let i = 0; i < 4; i++) {
            ctx.lineTo(0, -p.size * a * 2.5);
            ctx.lineTo(p.size * a * 0.5, 0);
            ctx.rotate(Math.PI / 2);
          }
          ctx.fill();
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, Math.max(0.5, p.size * a), 0, 7);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      raf.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf.current); window.removeEventListener('resize', resize); };
  }, []);

  useEffect(() => {
    if (!trigger) return;
    const cvs = ref.current; if (!cvs) return;
    const W = cvs.offsetWidth, H = cvs.offsetHeight;

    const cls = t.particles;
    const presets = {
      hit:    { n: 24, colors: ['#ef4444', '#f97316', '#fbbf24'], speed: 5.5, size: 3, shape: 'circle', g: 0.14 },
      crit:   { n: 70, colors: ['#fbbf24', '#fff', '#f97316', '#ef4444'], speed: 11, size: 4.5, shape: 'spark', g: 0.08 },
      heal:   { n: 28, colors: ['#34d399', '#6ee7b7', '#fff'], speed: 3.5, size: 3.5, shape: 'circle', g: -0.13 },
      magic:  { n: 42, colors: cls, speed: 6, size: 3.5, shape: 'star', g: 0.02 },
      block:  { n: 20, colors: ['#94a3b8', '#e2e8f0', '#fff'], speed: 4.5, size: 3, shape: 'spark', g: 0.2 },
      ult:    { n: 150, colors: [...cls, '#fff', '#fbbf24'], speed: 15, size: 5, shape: 'spark', g: 0.04 },
      bleed:  { n: 16, colors: ['#dc2626', '#991b1b'], speed: 2.5, size: 3, shape: 'circle', g: 0.32 },
      burn:   { n: 22, colors: ['#f97316', '#fbbf24', '#dc2626'], speed: 3, size: 3.5, shape: 'circle', g: -0.16 },
      class:  { n: 34, colors: cls, speed: 6, size: 4, shape: 'star', g: 0.03 },
      drop:   { n: 60, colors: ['#fbbf24', '#fff', '#f59e0b'], speed: 8, size: 4, shape: 'star', g: 0.1 },
    };

    const p = presets[trigger.type] || presets.hit;
    const cx = (trigger.x ?? 0.5) * W, cy = (trigger.y ?? 0.45) * H;

    for (let i = 0; i < p.n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = p.speed * (0.4 + Math.random() * 0.95);
      const life = 32 + Math.random() * 38;
      parts.current.push({
        x: cx, y: cy,
        vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
        g: p.g, life, max: life, rot: Math.random() * 6,
        size: p.size * (0.5 + Math.random()),
        color: p.colors[(Math.random() * p.colors.length) | 0],
        shape: p.shape,
      });
    }
  }, [trigger?.id]);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none z-30" />;
}

/* ---------- 2. FLOATING NUMBERS ---------- */
export function FloatingNumbers({ items }) {
  return (
    <AnimatePresence>
      {items.map(f => {
        const style = {
          crit:   'text-4xl sm:text-5xl text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,.95)]',
          dmg:    'text-2xl text-red-400 drop-shadow-lg',
          heal:   'text-2xl text-emerald-300 drop-shadow-lg',
          miss:   'text-xl text-slate-300',
          block:  'text-xl text-sky-300',
          status: 'text-base text-rose-300',
          taunt:  'text-5xl',
          drop:   'text-2xl text-amber-300 drop-shadow-[0_0_14px_rgba(251,191,36,.9)]',
        }[f.type] || 'text-2xl text-white';

        const dx = f.side === 'right' ? 62 : f.side === 'left' ? -62 : 0;

        return (
          <motion.div key={f.id}
            initial={{ opacity: 0, y: 26, scale: 0.4, x: dx }}
            animate={{
              opacity: [0, 1, 1, 0],
              y: f.type === 'crit' ? [-4, -76, -100] : [-4, -56, -74],
              scale: f.type === 'crit' ? [0.4, 1.55, 1.25] : [0.5, 1.15, 1],
              x: dx + (Math.random() * 26 - 13),
              rotate: f.type === 'crit' ? [0, -7, 5, 0] : 0,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: f.type === 'crit' ? 1.5 : 1.15, times: [0, .2, .7, 1] }}
            className={`absolute left-1/2 top-1/3 -translate-x-1/2 font-black z-40 pointer-events-none whitespace-nowrap ${style}`}>
            {f.type === 'crit' && <span className="mr-1">💥</span>}{f.text}
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

/* ---------- 3. SLASH FX ---------- */
export function SlashFX({ slash, classKey }) {
  if (!slash) return null;
  const t = themeOf(classKey);
  const rot = { head: -30, body: 0, legs: 30 }[slash.zone] ?? 0;

  const grad = slash.move === 'ultimate'
    ? `linear-gradient(90deg, transparent, #fff, ${t.hex}, transparent)`
    : `linear-gradient(90deg, transparent, ${t.glow}, transparent)`;

  return (
    <AnimatePresence>
      <motion.div key={slash.id}
        initial={{ opacity: 0, scaleX: 0.1, x: -180 }}
        animate={{ opacity: [0, 1, 0], scaleX: [0.1, 1.5, 1.7], x: [-180, 60, 220] }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.42, ease: 'easeOut' }}
        style={{ rotate: rot, background: grad, filter: 'blur(1px)' }}
        className="absolute left-0 right-0 top-1/3 h-3 z-[35] pointer-events-none" />
    </AnimatePresence>
  );
}

/* ---------- 4. SCREEN FX HOOK ---------- */
export function useScreenFX() {
  const [flash, setFlash] = useState(null);
  const [shake, setShake] = useState(0);

  const fire = (type, intensity = 1) => {
    const colors = {
      hit: 'rgba(239,68,68,.28)', crit: 'rgba(251,191,36,.55)',
      heal: 'rgba(52,211,153,.28)', ult: 'rgba(255,255,255,.78)',
      block: 'rgba(148,163,184,.25)', ko: 'rgba(0,0,0,.7)',
      drop: 'rgba(251,191,36,.4)',
    };
    setFlash({ id: Math.random(), color: colors[type] || colors.hit });
    setShake(intensity);
    setTimeout(() => setFlash(null), 220);
    setTimeout(() => setShake(0), 440);
  };

  const FlashLayer = () => (
    <AnimatePresence>
      {flash && (
        <motion.div key={flash.id}
          initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="absolute inset-0 z-[25] pointer-events-none"
          style={{ background: flash.color }} />
      )}
    </AnimatePresence>
  );

  const shakeStyle = shake
    ? { animation: `wof-shake ${shake > 1.5 ? 0.5 : 0.34}s cubic-bezier(.36,.07,.19,.97)` }
    : {};

  return { fire, FlashLayer, shakeStyle };
}

/* ---------- 5. ULTIMATE CINEMATIC ---------- */
export function UltimateOverlay({ show, name, heroImg, classKey, onDone }) {
  const t = themeOf(classKey);
  useEffect(() => {
    if (show) { const x = setTimeout(onDone, 2200); return () => clearTimeout(x); }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">

          <motion.div className="absolute inset-0 bg-black"
            initial={{ opacity: 0 }} animate={{ opacity: [0, .85, .85, 0] }}
            transition={{ duration: 2.2, times: [0, .12, .78, 1] }} />

          {Array.from({ length: 18 }).map((_, i) => (
            <motion.div key={i}
              className="absolute h-[2px]"
              style={{
                top: `${5 + i * 5.4}%`, left: 0, right: 0,
                background: `linear-gradient(90deg, transparent, ${t.glow}, transparent)`,
              }}
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: '100%', opacity: [0, 1, 0] }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.026 }} />
          ))}

          <motion.div className="absolute inset-0 flex items-center justify-center gap-5"
            initial={{ scale: 1.35, opacity: 0 }}
            animate={{ scale: [1.35, 1, 1, 1.14], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.2, times: [0, .18, .74, 1] }}>

            {heroImg && (
              <motion.img src={heroImg} alt=""
                className="w-32 h-44 sm:w-40 sm:h-56 object-cover rounded-2xl ring-4"
                style={{ borderColor: t.hex, boxShadow: `0 0 70px ${t.hex}` , '--tw-ring-color': t.hex }}
                animate={{ rotate: [-3, 3, -2, 0] }} transition={{ duration: 2 }} />
            )}

            <div className="text-center">
              <motion.p
                className="font-display text-5xl sm:text-7xl font-black text-transparent bg-clip-text"
                style={{
                  backgroundImage: `linear-gradient(180deg, #fff, ${t.glow}, ${t.hex})`,
                  filter: `drop-shadow(0 4px 24px ${t.hex})`,
                }}
                animate={{ scale: [0.7, 1.2, 1], letterSpacing: ['0px', '8px', '4px'] }}
                transition={{ duration: 0.7 }}>
                ULTIMATE
              </motion.p>
              <motion.p className="font-black text-lg mt-1 tracking-widest"
                style={{ color: t.glow }}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}>
                {name}
              </motion.p>
            </div>
          </motion.div>

          <motion.div className="absolute left-1/2 top-1/2 rounded-full border-4"
            style={{ borderColor: t.hex }}
            initial={{ width: 0, height: 0, x: '-50%', y: '-50%', opacity: 1 }}
            animate={{ width: 1500, height: 1500, opacity: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: 'easeOut' }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- 6. STATUS AURA ---------- */
export function StatusAura({ statuses = [] }) {
  const auras = {
    burn:   { c: 'rgba(249,115,22,.85)', r: '#fb923c' },
    bleed:  { c: 'rgba(220,38,38,.8)',   r: '#ef4444' },
    stun:   { c: 'rgba(250,204,21,.8)',  r: '#facc15' },
    slow:   { c: 'rgba(56,189,248,.75)', r: '#38bdf8' },
    shield: { c: 'rgba(148,163,184,.85)', r: '#cbd5e1' },
  };
  const active = statuses.find(s => auras[s.status]);
  if (!active) return null;
  const a = auras[active.status];

  return (
    <motion.div className="absolute inset-0 rounded-xl ring-2 pointer-events-none"
      style={{ boxShadow: `0 0 26px ${a.c}`, borderColor: a.r, '--tw-ring-color': a.r }}
      animate={{ opacity: [0.45, 1, 0.45] }}
      transition={{ duration: 1.3, repeat: Infinity }} />
  );
}

/* ---------- 7. KO ---------- */
export function KOEffect({ show }) {
  if (!show) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="absolute inset-0 z-40 grid place-items-center pointer-events-none">
      <motion.div className="absolute inset-0 bg-red-900/60 rounded-xl"
        animate={{ opacity: [0, .85, .55] }} transition={{ duration: .5 }} />
      <motion.span className="text-5xl relative"
        initial={{ scale: 0, rotate: -40 }}
        animate={{ scale: [0, 1.5, 1.1], rotate: [-40, 12, 0] }}
        transition={{ duration: .6, ease: 'backOut' }}>💀</motion.span>
    </motion.div>
  );
}

/* ---------- 8. COMBO METER ---------- */
export function ComboMeter({ combo, classKey }) {
  const t = themeOf(classKey);
  return (
    <AnimatePresence>
      {combo > 1 && (
        <motion.div key={combo}
          initial={{ scale: 0.4, opacity: 0, rotate: -12 }}
          animate={{ scale: [0.4, 1.35, 1], opacity: 1, rotate: 0 }}
          exit={{ scale: 0.6, opacity: 0 }}
          className="absolute top-3 right-3 z-40 pointer-events-none">
          <div className="px-3 py-1 rounded-xl"
            style={{ background: `linear-gradient(90deg, ${t.hex}, ${t.glow})`,
                     boxShadow: `0 0 24px ${t.hex}` }}>
            <p className="font-display font-black text-white text-lg leading-none">{combo}× COMBO</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- 9. HP BAR ---------- */
export function HPBar({ hp, maxHp, label }) {
  const pct = Math.max(0, (hp / maxHp) * 100);
  const [ghost, setGhost] = useState(pct);
  useEffect(() => { const x = setTimeout(() => setGhost(pct), 400); return () => clearTimeout(x); }, [pct]);

  const color = pct > 50 ? 'from-emerald-400 to-emerald-500'
    : pct > 25 ? 'from-amber-400 to-orange-500' : 'from-red-500 to-rose-600';

  return (
    <div className={`relative h-3 rounded-full bg-black/45 overflow-hidden ${pct < 25 ? 'animate-pulse' : ''}`}>
      <motion.div className="absolute inset-y-0 left-0 bg-white/45"
        animate={{ width: `${ghost}%` }} transition={{ duration: .55, ease: 'easeOut' }} />
      <motion.div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${color}`}
        animate={{ width: `${pct}%` }} transition={{ duration: .28 }} />
      {label && (
        <span className="absolute inset-0 grid place-items-center text-[9px] font-black text-white drop-shadow">
          {label}
        </span>
      )}
    </div>
  );
}

/* ---------- 10. FOCUS ORB ---------- */
export function FocusOrb({ focus, classKey }) {
  const t = themeOf(classKey);
  const ready = focus >= 100;
  return (
    <div className="relative h-1.5 rounded-full bg-black/45 overflow-hidden">
      <motion.div className="h-full rounded-full"
        style={ready
          ? { background: `linear-gradient(90deg, ${t.hex}, #fff, ${t.hex})`, backgroundSize: '200% 100%' }
          : { background: `linear-gradient(90deg, ${t.hex}, ${t.glow})` }}
        animate={ready
          ? { width: '100%', backgroundPosition: ['0% 0%', '200% 0%'] }
          : { width: `${focus}%` }}
        transition={ready
          ? { backgroundPosition: { duration: 1.1, repeat: Infinity, ease: 'linear' } }
          : { duration: .3 }} />
      {ready && (
        <motion.div className="absolute inset-0 rounded-full ring-1"
          style={{ '--tw-ring-color': t.glow }}
          animate={{ opacity: [.3, 1, .3] }} transition={{ duration: .9, repeat: Infinity }} />
      )}
    </div>
  );
}

/* ---------- 11. GEAR CHIPS ---------- */
export function GearChips({ equipment }) {
  if (!equipment) return null;
  const items = Object.values(equipment).filter(Boolean);
  if (!items.length) return null;

  return (
    <div className="flex gap-0.5 justify-center mt-1">
      {items.map(it => {
        const r = rarityOf(it.rarity);
        return (
          <div key={it.slot}
            className="w-4 h-4 rounded border overflow-hidden"
            style={{ borderColor: r.hex, boxShadow: r.tier >= 4 ? `0 0 6px ${r.hex}` : 'none' }}
            title={`${it.name} (${r.label})`}>
            {it.thumb || it.image
              ? <img src={it.thumb || it.image} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full" style={{ background: `${r.hex}44` }} />}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- 12. ITEM DROP REVEAL ---------- */
export function ItemDropReveal({ drop, onClose }) {
  if (!drop) return null;
  const r = rarityOf(drop.rarity);

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center p-6">

        <motion.div
          initial={{ scale: 0.3, opacity: 0, rotate: -25 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 210, damping: 16 }}
          className="relative text-center">

          <motion.div className="absolute -inset-16 rounded-full"
            style={{ background: `radial-gradient(circle, ${r.hex}55, transparent 70%)` }}
            animate={{ scale: [0.8, 1.35, 1], opacity: [0, .95, .55] }}
            transition={{ duration: 1.1 }} />

          {Array.from({ length: 14 }).map((_, i) => (
            <motion.div key={i} className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full"
              style={{ background: r.hex }}
              initial={{ x: 0, y: 0, opacity: 1 }}
              animate={{
                x: Math.cos(i / 14 * Math.PI * 2) * 130,
                y: Math.sin(i / 14 * Math.PI * 2) * 130,
                opacity: 0, scale: [1, 1.6, 0],
              }}
              transition={{ duration: 1.1, delay: 0.2 }} />
          ))}

          <motion.p className="relative font-black text-sm uppercase tracking-[0.3em] mb-3"
            style={{ color: r.hex }}
            animate={{ opacity: [0, 1] }} transition={{ delay: 0.3 }}>
            {r.label} Drop!
          </motion.p>

          <div className="relative w-44 h-44 mx-auto rounded-3xl overflow-hidden border-4"
            style={{ borderColor: r.hex, boxShadow: `0 0 60px ${r.hex}` }}>
            <img src={drop.image} alt={drop.name} className="w-full h-full object-cover" />
            {r.tier >= 4 && (
              <motion.div className="absolute inset-0"
                style={{ background: `linear-gradient(115deg, transparent 40%, ${r.hex}66 50%, transparent 60%)` }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 1.8, repeat: Infinity }} />
            )}
          </div>

          <motion.p className="relative font-display text-2xl font-extrabold text-white mt-4"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            {drop.icon} {drop.name}
          </motion.p>

          <motion.button onClick={onClose}
            className="relative btn bg-white text-ink-900 font-black mt-5 px-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            Awesome!
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}